import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useApi, type PropertyResponse } from '@/shared/api';
import { useObjectScope } from '@/shared/object-scope';
import {
  applyLocalFilters,
  isPropertiesFiltered,
  propertiesSearchFromState,
  propertyFiltersFromSearch,
  propertyPageFromSearch,
  type PropertiesSearch,
  type PropertyFilterState,
} from './propertiesListSearch';

export const PROPERTIES_PAGE_SIZE = 25;

export type PropertiesEmptyKind = 'error' | 'filtered' | 'empty' | 'pageEmpty' | null;

/**
 * Der Abfrageschlüssel der Apartmentliste — die Detailansicht baut ihn für die Nachbarschaft
 * ihrer Pfeile identisch nach und trifft damit denselben Cache-Eintrag (F4).
 */
export function propertiesListArgs(input: {
  page: number;
  filters: PropertyFilterState;
  propertyIds: string[] | null;
}) {
  return {
    page: input.page,
    pageSize: PROPERTIES_PAGE_SIZE,
    ...(input.filters.query.trim() ? { search: input.filters.query.trim() } : {}),
    propertyIds: input.propertyIds ?? undefined,
    lifecycle: input.filters.lifecycle,
  };
}

/**
 * Listenzustand der Apartmentliste: URL-State, Serverabfrage und lokale Nachfilterung.
 *
 * <p>Steht getrennt von der Seite, weil hier der Vertrag liegt, der nichts mit Layout zu tun hat:
 * „ein geteilter Link stellt denselben Arbeitskontext her" (§5) — und weil die Detailansicht
 * denselben Kontext lesen muss, um ihre Nachbarn zu bestimmen.</p>
 */
export function usePropertiesList() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as PropertiesSearch;
  const { propertyIds } = useObjectScope();

  const filters = propertyFiltersFromSearch(search);
  const page = propertyPageFromSearch(search);
  const filtered = isPropertiesFiltered(filters);

  const apply = (next: { filters?: PropertyFilterState; page?: number }) =>
    void navigate({
      to: '/properties',
      search: propertiesSearchFromState({
        filters: next.filters ?? filters,
        page: next.page ?? page,
      }) as never,
    });

  // Wechselt der Objekt-Scope, kann die aktuelle Seite im neuen Bestand leer sein. Der erste
  // Lauf wird bewusst übersprungen: ein geteilter Link mit `?seite=3` soll auf Seite 3 landen
  // und nicht beim Mounten auf Seite 1 zurückgesetzt werden (Befund aus Block A).
  const scopeKey = propertyIds?.join(',') ?? 'all';
  const knownScope = useRef<string | null>(null);
  useEffect(() => {
    const previous = knownScope.current;
    knownScope.current = scopeKey;
    if (previous === null || previous === scopeKey) return;
    if (page > 1) apply({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const args = propertiesListArgs({ page, filters, propertyIds: propertyIds ?? null });
  const { data, isLoading, error } = useQuery({
    queryKey: ['properties', 'list', args],
    queryFn: () => api.properties.list(args),
    placeholderData: (prev) => prev,
  });

  const pageItems: PropertyResponse[] = data?.items ?? [];
  const items = applyLocalFilters(pageItems, filters);
  const listIsEmpty = !isLoading && data !== undefined && items.length === 0;
  const emptyKind: PropertiesEmptyKind =
    // `placeholderData` hält die vorige Seite stehen. Scheitert ein Aktualisieren im
    // Hintergrund, wäre es falsch, eine gefüllte Liste durch einen Fehlerzustand zu ersetzen —
    // der Fehler gilt nur, solange nichts dasteht.
    error && data === undefined
      ? 'error'
      : listIsEmpty && filtered
        ? 'filtered'
        : listIsEmpty
          ? // Auch eine leere Folgeseite bekommt eine Erklärung: vorher stand dort eine
            // Tabelle ohne Zeilen und ohne Wort dazu.
            page === 1
            ? 'empty'
            : 'pageEmpty'
          : null;

  return {
    items,
    /** Ungefiltert geladene Seite — Grundlage der Städteauswahl, sonst filterte sie sich selbst weg. */
    pageItems,
    total: data?.total ?? 0,
    hasData: data !== undefined,
    isLoading,
    emptyKind,
    filters,
    filtered,
    page,
    pageSize: PROPERTIES_PAGE_SIZE,
    // Ein Filterwechsel setzt auf Seite 1 zurück — sonst landet man auf einer Seite, die es im
    // neuen Ergebnis nicht mehr gibt.
    setFilters: (next: PropertyFilterState) => apply({ filters: next, page: 1 }),
    setPage: (next: number) => apply({ page: next }),
    /** Der Arbeitskontext, den jede Zeile an die Detailansicht weiterreicht (F4). */
    listContext: propertiesSearchFromState({ filters, page }),
  };
}
